import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';
import { enquiriesAPI } from '../utils/api';

const STATUS_OPTIONS = ['New', 'Contacted', 'In Progress', 'Enrolled', 'Resolved', 'Rejected'];

const getNormalizedStatus = (status) => {
  if (!status) return 'New';
  const s = status.toString().trim().toLowerCase();
  if (s === 'new') return 'New';
  if (s === 'contacted') return 'Contacted';
  if (s === 'in progress' || s === 'inprogress') return 'In Progress';
  if (s === 'enrolled') return 'Enrolled';
  if (s === 'resolved') return 'Resolved';
  if (s === 'rejected') return 'Rejected';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const STATUS_BADGE = { 
  New: 'badge-blue', 
  Contacted: 'badge-orange', 
  'In Progress': 'badge-orange', 
  Enrolled: 'badge-green', 
  Resolved: 'badge-green', 
  Rejected: 'badge-red' 
};

const STATUS_ICON = { 
  New: '🔵', 
  Contacted: '📞', 
  'In Progress': '🟠', 
  Enrolled: '🎓', 
  Resolved: '✅', 
  Rejected: '❌' 
};

function EnquiryCard({ enq, onUpdate, onDelete }) {
  const { t } = useTranslation();
  const [changing, setChanging] = useState(false);
  const currentStatus = getNormalizedStatus(enq.status);

  const handleStatus = async newStatus => {
    setChanging(true);
    try { await onUpdate(enq._id || enq.id, { status: newStatus }); }
    finally { setChanging(false); }
  };

  return (
    <div style={{ background:'#fff', border:'1px solid #e0d8ce', padding:'24px', marginBottom:16, transition:'box-shadow .2s' }}
      onMouseOver={e => e.currentTarget.style.boxShadow='0 4px 20px rgba(28,23,18,.08)'}
      onMouseOut={e => e.currentTarget.style.boxShadow=''}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12, marginBottom:12 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
            <span style={{ fontWeight:700, fontSize:15, color:'#111111' }}>{enq.enquiryNumber || '#—'}</span>
            <span className={`badge ${STATUS_BADGE[currentStatus] || 'badge-blue'}`}>{STATUS_ICON[currentStatus] || '🔵'} {t('enquiries.status_' + currentStatus.replace(' ', ''), currentStatus).toUpperCase()}</span>
          </div>
          <div style={{ fontSize:13, color:'#7a7068' }}>
            {new Date(enq.createdAt).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <select
            value={currentStatus}
            disabled={changing}
            onChange={e => handleStatus(e.target.value)}
            className="form-input-style"
            style={{ padding:'6px 12px', fontSize:12, width:'auto' }}
          >
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{t('enquiries.status_' + s.replace(' ', ''), s)}</option>)}
          </select>
          <button
            className="btn btn-danger btn-sm"
            onClick={() => { if (window.confirm(t('enquiries.deleteConfirm'))) onDelete(enq._id || enq.id); }}
            title="Delete"
          >
            🗑
          </button>
        </div>
      </div>

      {/* Contact info */}
      <div style={{ display:'flex', gap:20, flexWrap:'wrap', marginBottom:12, padding:'10px 14px', background:'#f8f4ef', borderLeft:'3px solid #e0d8ce' }}>
        {enq.name && (
          <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:13 }}>
            <span style={{ color:'#7a7068' }}>👤</span>
            <span style={{ fontWeight:600, color:'#111111' }}>{enq.name}</span>
          </div>
        )}
        {enq.mobile && (
          <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:13 }}>
            <span style={{ color:'#7a7068' }}>📞</span>
            <a href={`tel:${enq.mobile}`} style={{ color:'#c04060', fontWeight:500 }}>{enq.mobile}</a>
          </div>
        )}
        {enq.email && (
          <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:13 }}>
            <span style={{ color:'#7a7068' }}>✉️</span>
            <a href={`mailto:${enq.email}`} style={{ color:'#c04060', fontWeight:500 }}>{enq.email}</a>
          </div>
        )}
      </div>

      {/* Preferences Section */}
      <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' }}>
        {enq.interest && (
          <div style={{ background:'#fff4f7', border:'1px solid #f0e0e8', padding:'6px 12px', borderRadius:2, fontSize:12, color:'#7b1d3a' }}>
            <span style={{ marginRight:6 }}>🎯</span> {t('enquiries.interestedIn', 'Interested in:')} <strong>{t(enq.interest, enq.interest)}</strong>
          </div>
        )}
        {enq.level && (
          <div style={{ background:'#eef7ff', border:'1px solid #d0e8ff', padding:'6px 12px', borderRadius:2, fontSize:12, color:'#1e56a0' }}>
            <span style={{ marginRight:6 }}>💎</span> {t('enquiries.level', 'Level:')} <strong>{t(enq.level, enq.level)}</strong>
          </div>
        )}
      </div>

      {/* Message */}
      <div style={{ fontSize:14, color:'#3d3730', lineHeight:1.7, background:'#f4f4f4', padding:'12px 16px' }}>
        "{enq.message}"
      </div>
    </div>
  );
}

export default function Enquiries() {
  const { t } = useTranslation();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoad]        = useState(true);
  const [filter, setFilter]       = useState('All');
  const [search, setSearch]       = useState('');

  useEffect(() => {
    let isMounted = true;
    enquiriesAPI.getAll()
      .then(r => { if (isMounted) setEnquiries(r.data); })
      .catch(err => { if (isMounted) console.error(err); })
      .finally(() => { if (isMounted) setLoad(false); });
    return () => { isMounted = false; };
  }, []);

  const handleUpdate = async (id, data) => {
    try {
      const res = await enquiriesAPI.update(id, data);
      setEnquiries(p => p.map(e => (e._id || e.id) === id ? res.data : e));
    } catch(err) { console.error(err); }
  };

  const handleDelete = async id => {
    try {
      await enquiriesAPI.delete(id);
      setEnquiries(p => p.filter(e => (e._id || e.id) !== id));
    } catch(err) { console.error(err); }
  };

  const filtered = enquiries.filter(e => {
    const statusNorm = getNormalizedStatus(e.status);
    const matchFilter = filter === 'All' || statusNorm === filter;
    const q = search.toLowerCase();
    const matchSearch = !q || [e.name, e.mobile, e.email, e.message, e.enquiryNumber].some(v => v?.toLowerCase().includes(q));
    return matchFilter && matchSearch;
  });

  const counts = STATUS_OPTIONS.reduce((acc, s) => ({ ...acc, [s]: enquiries.filter(e => getNormalizedStatus(e.status) === s).length }), {});

  return (
    <Layout title={t('enquiries.title')}>
      <div className="dash-page-header">
        <div>
          <h1 className="dash-page-title">{t('enquiries.title')}</h1>
          <p className="text-sm text-muted" style={{ marginTop:4 }}>{enquiries.length} {t('enquiries.total')} · {counts['New'] || 0} {t('enquiries.new')}</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:16, marginBottom:28 }}>
        {[['All', '📋', enquiries.length, '#c04060'], ...STATUS_OPTIONS.map(s => [s, STATUS_ICON[s], counts[s]||0, s==='New'?'#3b7dd8':(s==='Contacted'||s==='In Progress')?'#e07d2a':(s==='Enrolled'||s==='Resolved')?'#4caf86':'#c53030'])].map(([label, icon, count, color]) => (
          <div key={label}
            className="dash-stat-card"
            style={{ cursor:'pointer', border: filter===label ? `2px solid ${color}` : '1px solid rgba(255,255,255,.05)', transition:'all .2s' }}
            onClick={() => setFilter(label)}>
            <div className="dash-stat-icon" style={{ background: color+'22', color, fontSize:22 }}>{icon}</div>
            <div>
              <div className="dash-stat-val" style={{ fontSize:28 }}>{count}</div>
              <div className="dash-stat-label">{t('enquiries.status_' + label.replace(' ', ''), label)}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="dash-card" style={{ padding:'16px 20px', marginBottom:20 }}>
        <input
          className="form-input-style"
          placeholder={t('enquiries.search')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ border:'none', background:'transparent', padding:0, fontSize:14 }}
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="dash-loading">⏳ {t('enquiries.loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="dash-card">
          <div className="dash-empty">
            <div className="dash-empty-icon">💬</div>
            <div className="dash-empty-title">{search || filter !== 'All' ? t('enquiries.noEnquiriesSub', 'No matching enquiries') : t('enquiries.noEnquiries', 'No enquiries yet')}</div>
            <p className="dash-empty-sub">{t('enquiries.emptySub', 'Enquiries submitted from the public landing page will appear here.')}</p>
          </div>
        </div>
      ) : (
        <div>
          {filtered.map((enq, index) => (
            <EnquiryCard key={enq._id || enq.id || index} enq={enq} onUpdate={handleUpdate} onDelete={handleDelete} />
          ))}
        </div>
      )}
    </Layout>
  );
}
